import { ImgID } from "../image_loader/img_id.js";
import { formatMinutesShort } from "../common/reward_format.js";
import type { ClaimFreeOfferResponse, OfferDto, OfferRewardDto, OfferRewardType } from "../api/dto/offers.dto.js";

/** Lobby entry point and renderer of the rewarded "watch an ad, open a random box" offer. */
export const MYSTERY_BOX_PLACEMENT = 'lobby_mystery_box';
export const MYSTERY_BOX_TEMPLATE = 'mystery_box';
/** Ads placement id reported to MAX and the rewarded analytics. */
export const MYSTERY_BOX_AD_PLACEMENT = 'mystery_box';

/** One cell of the "Possible rewards" row: the range the server rolls from. */
export interface MysteryBoxPossibleReward
{
  type: OfferRewardType;
  min: number;
  max: number;
}

/**
 * What the Figma layout shows. The server can override it per offer through
 * `data.possible_rewards` so LiveOps can retune the ranges without a client
 * release; the actual roll always happens on the server.
 */
const DEFAULT_POSSIBLE_REWARDS: MysteryBoxPossibleReward[] = [
  { type: 'coins', min: 20, max: 1000 },
  { type: 'infinite_hearts_minutes', min: 15, max: 60 },
  { type: 'multi_balls', min: 1, max: 3 },
  { type: 'fireworks', min: 1, max: 3 },
];

/** Display order of granted rewards, most valuable first. */
const REWARD_ORDER: OfferRewardType[] = [
  'coins', 'infinite_hearts_minutes', 'hearts', 'multi_balls', 'big_balls', 'fireworks', 'infinite_balls', 'no_ads_minutes', 'no_ads',
];

export function mysteryBoxAnalyticsContext( placement_id: string, trigger_event_id?: string ): Record<string, unknown>
{
  return {
    placement: placement_id,
    placement_id,
    template: MYSTERY_BOX_TEMPLATE,
    ...( trigger_event_id ? { trigger_event_id } : {} ),
  };
}

export function isMysteryBoxOffer( offer: OfferDto | null | undefined ): offer is OfferDto
{
  return Boolean( offer && offer.placement?.template === MYSTERY_BOX_TEMPLATE );
}

export type MysteryBoxMode = 'rewarded' | 'free' | 'paid';

export function mysteryBoxMode( offer: OfferDto | null | undefined ): MysteryBoxMode
{
  const type = offer?.purchase?.type;
  return type === 'free' ? 'free' : type === 'consumable' || type === 'non_consumable' ? 'paid' : 'rewarded';
}

export function getMysteryBoxPossibleRewards( offer: OfferDto | null | undefined ): MysteryBoxPossibleReward[]
{
  const raw = offer?.mystery_box?.rewards ?? offer?.data?.possible_rewards;
  if ( !Array.isArray( raw ) ) return DEFAULT_POSSIBLE_REWARDS;

  const parsed = raw.flatMap( ( value ): MysteryBoxPossibleReward[] =>
  {
    if ( !value || typeof value !== 'object' ) return [];
    const { type, min, max } = value as Record<string, unknown>;
    if ( typeof type !== 'string' || !REWARD_ORDER.includes( type as OfferRewardType ) ) return [];
    if ( typeof min !== 'number' || typeof max !== 'number' || min <= 0 || max < min ) return [];
    return [{ type: type as OfferRewardType, min, max }];
  } );
  return parsed.length ? parsed : DEFAULT_POSSIBLE_REWARDS;
}

/**
 * The server's roll, in display order. `granted` also carries absolute
 * timestamps (`infinite_hearts_until`), which are not rewards and are skipped.
 */
export function mysteryBoxGrantedRewards( granted: ClaimFreeOfferResponse['granted'] | undefined ): OfferRewardDto[]
{
  if ( !granted ) return [];
  const entries = granted as Record<string, unknown>;
  return REWARD_ORDER.flatMap( ( type ): OfferRewardDto[] =>
  {
    const amount = entries[type];
    if ( type === 'no_ads' ) return amount === true ? [{ type, amount: 1 }] : [];
    return typeof amount === 'number' && amount > 0 ? [{ type, amount }] : [];
  } );
}

/** Debug-only stand-in for the server roll, used by the preview dialog. */
export function rollMysteryBoxPreviewRewards( possible: MysteryBoxPossibleReward[] ): OfferRewardDto[]
{
  const roll = ( reward: MysteryBoxPossibleReward ): number =>
  {
    const value = reward.min + Math.random() * ( reward.max - reward.min );
    return reward.type === 'infinite_hearts_minutes' ? Math.round( value / 15 ) * 15 || reward.min : Math.round( value );
  };
  return possible.map( reward => ( { type: reward.type, amount: roll( reward ) } ) );
}

export function mysteryBoxRewardIcon( type: OfferRewardType ): ImgID
{
  switch ( type )
  {
    case 'coins': return ImgID.UI_ShopBlockImg;
    case 'hearts': return ImgID.IconHeart;
    case 'infinite_hearts_minutes': return ImgID.UI_ShopInfiniteHeart;
    case 'big_balls': return ImgID.UI_GameStartBigBallBooster;
    case 'infinite_balls': return ImgID.UI_GameStartInfiniteBallsBooster;
    case 'multi_balls': return ImgID.UI_GameStartMultiballsBooster;
    case 'fireworks': return ImgID.UI_GameStartFireworkBooster;
    case 'mines': return ImgID.UI_PassiveBoosterBomb;
    case 'level_shots': return ImgID.UI_DailyTasksTaskCannon;
    case 'no_ads': case 'no_ads_minutes': return ImgID.UI_RemoveAdsIcon;
  }
}

/** Icon size inside a square slot of `slot` px: coins are a wide pile, the shop heart has a tight crop. */
export function mysteryBoxIconSize( type: OfferRewardType, slot: number ): { w: number; h: number }
{
  if ( type === 'coins' ) return { w: slot, h: slot * 168 / 210 };
  if ( type === 'infinite_hearts_minutes' || type === 'hearts' ) return { w: slot * 0.8, h: slot * 0.8 };
  return { w: slot, h: slot };
}

export function mysteryBoxAmountText( reward: OfferRewardDto ): string
{
  if ( reward.type === 'infinite_hearts_minutes' || reward.type === 'no_ads_minutes' ) return formatMinutesShort( reward.amount );
  if ( reward.type === 'no_ads' ) return 'NO ADS';
  return reward.type === 'coins' ? String( reward.amount ) : `x${reward.amount}`;
}

export function mysteryBoxRangeText( reward: MysteryBoxPossibleReward ): string
{
  const format = ( value: number ): string =>
    reward.type === 'infinite_hearts_minutes' || reward.type === 'no_ads_minutes' ? formatMinutesShort( value ) : String( value );
  return reward.min === reward.max ? format( reward.min ) : `${format( reward.min )}-${format( reward.max )}`;
}
